/* eslint-disable @typescript-eslint/no-explicit-any */
// Vendor
import he from 'he';
import { QVueGlobals } from 'quasar';

// Personal libs
import { DeviceOrientation } from '@shared/enum/device-orientation.enum';

// Fonction remplacant les caractères accentués en code HTML
export function escapeHtml(unsafe: string) {
  return he.encode(unsafe, {
    useNamedReferences: true,
    allowUnsafeSymbols: false,
  });
}

// Fonction remplacant les codes HTML en caractères accentués
export function unescapeHtml(safe: string): string {
  return he.decode(safe);
}

export function stripslashes(str: string) {
  //       discuss at: http://locutus.io/php/stripslashes/
  //      original by: Kevin van Zonneveld (http://kvz.io)
  //      improved by: Ates Goral (http://magnetiq.com)
  //      improved by: marrtins
  //      improved by: rezna
  //         fixed by: Mick@el
  //      bugfixed by: Onno Marsman (https://twitter.com/onnomarsman)
  //      bugfixed by: Brett Zamir (http://brett-zamir.me)
  //         input by: Rick Waldron
  //         input by: Brant Messenger (http://www.brantmessenger.com/)
  // reimplemented by: Brett Zamir (http://brett-zamir.me)
  //        example 1: stripslashes('Kevin\'s code')
  //        returns 1: "Kevin's code"
  //        example 2: stripslashes('Kevin\\\'s code')
  //        returns 2: "Kevin\'s code"

  return (str + '').replace(/\\(.?)/g, function (s, n1) {
    switch (n1) {
      case '\\':
        return '\\';
      case '0':
        return '\u0000';
      case '':
        return '';
      default:
        return n1;
    }
  });
}

export function addslashes(str: string) {
  return str
    .replace(/\\/g, '\\\\')
    .replace(/\t/g, '\\t')
    .replace(/\n/g, '\\n')
    .replace(/\f/g, '\\f')
    .replace(/\r/g, '\\r')
    .replace(/'/g, "\\'")
    .replace(/"/g, '\\"')
    .replace(/\0/g, '\\0');
}

export function sanitize(value: string): string {
  return addslashes(escapeHtml(value));
}
export function unsanitize(value: string): string {
  return stripslashes(unescapeHtml(value));
}

export function replaceAll(
  s: string,
  searchString: string,
  newString: string,
): string {
  const t = s.split(searchString);
  return t.join(newString);
}

/**
 * Sanitize string
 */
export const sanitizeString = (str: string): string =>
  sanitize(unsanitize(str));

/**
 * Returns a number between min and max
 * @param min
 * @param max
 * @returns
 */
export const alea = (min: number, max: number): number =>
  Math.floor(Math.random() * max) + min;

/**
 *
 * @param ms timeout time in milliseconds
 * @returns
 */
export const sleep = (ms = 0) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Permet de styliser des dialogs
 */
interface IContentStyleParams {
  quasarInstance: QVueGlobals;
  orientation?: DeviceOrientation;
  isLoading?: boolean;
}
export interface IContentStyleReturns {
  height?: string;
  maxHeight?: string;
}
export const defineDialogContentHeights = (
  params: IContentStyleParams,
): IContentStyleReturns => {
  const returnedObject: IContentStyleReturns = {
    height: undefined,
    maxHeight: undefined,
  };

  if (params) {
    if (!params.quasarInstance.screen.gt.xs) {
      if (params.orientation === DeviceOrientation.PORTRAIT) {
        returnedObject.height = params.isLoading ? '0vh' : '88vh';
        returnedObject.maxHeight = params.isLoading ? '0vh' : '95vh';
      } else {
        returnedObject.maxHeight = params.isLoading ? '0vh' : '100vh';
      }
    } else {
      // returnedObject.height = params.isLoading ? '0px' : Math.round(window.innerHeight * 65 / 100) + 'px'
      returnedObject.maxHeight = params.isLoading
        ? '0px'
        : Math.round((window.innerHeight * 80) / 100) + 'px';
    }
  }
  return returnedObject;
};

export const contentStyle = (
  params: IContentStyleParams,
): IContentStyleReturns => defineDialogContentHeights(params);

/**
 * Nom       : trim
 * Desc      : équivalent PHP de trim
 */
export function trim(str?: string): string | undefined {
  return str && str.indexOf(' ') >= 0 ? str.replace(/ /g, '') : str;
}

/**
 * Nom : trimStart
 */
export function trimStart(str: string): string {
  if (str.startsWith(' ')) {
    return trimStart(str.slice(1));
  }
  return str;
}

/**
 * Nom : trimEnd
 */
export function trimEnd(str: string): string {
  if (str.endsWith(' ')) {
    return trimEnd(str.substring(0, str.length - 1));
  }
  return str;
}

/**
 * Fonction calculant la taille d'un fichier en Ko ou en Mo
 */
export function tailleFichier(octets = 0) {
  let temp = octets;
  let rslt = temp.toString() + ' o';

  if (temp > 1000) {
    temp = Math.round((octets / 1024) * 100) / 100;
    rslt = temp.toString() + ' Ko';
    if (temp > 1000) {
      temp = Math.round((octets / 1024 / 1024) * 100) / 100;
      rslt = temp.toString() + ' Mo';
    }
  }

  return rslt;
}

export function sanitizeFileName(fileName: string, usedForUpload = false) {
  return usedForUpload
    ? fileName.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/'/g, "_").replace(/ /g, "_")
    : encodeURI(fileName.replace(/&/g, '').replace(/\?/g, ''));
}

/**
 * Scroll to top
 */
export function scrollToTop() {
  const c = document.documentElement.scrollTop || document.body.scrollTop;
  if (c > 0) {
    window.requestAnimationFrame(scrollToTop);
    window.scrollTo(0, c - c / 8);
  }
}

/**
 * Flatten an array
 * @param {Array} arr
 * @returns
 */
export function flatten<T>(arr?: Array<any>): Array<T> {
  if (arr != null && arr.length > 0) {
    return arr?.reduce(function (a, b) {
      return [].concat(
        Array.isArray(a) ? flatten(a) : a,
        Array.isArray(b) ? flatten(b) : b,
      );
    });
  } else {
    return [];
  }
}
